/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Info, ChevronRight, X, Sparkles } from 'lucide-react';
import api from '../../lib/api';

/**
 * DataQualityBanner — Détecte et affiche les saisies manquantes côté agent.
 *
 * Objectif : aider l'agent à comprendre POURQUOI consigner ses données
 * (visites, échanges vendeur) en pointant les manques concrets et en
 * proposant un raccourci d'action.
 *
 * - Dismissible par session (sessionStorage : pas par défaut, pour ne pas
 *   masquer définitivement un signal utile).
 * - Items cliquables → naviguent directement vers la fiche mandat sur le
 *   bon onglet.
 */
const SEVERITY_STYLE = {
    warning: {
        icon: AlertCircle,
        iconClass: 'text-amber-500',
        chipClass: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    },
    info: {
        icon: Info,
        iconClass: 'text-sky-500',
        chipClass: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20',
    },
};

export default function DataQualityBanner({ basePath = '/agent' }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem('homly_dq_banner_dismissed') === '1'; }
        catch { return false; }
    });

    const fetchGaps = useCallback(async () => {
        try {
            const res = await api.get('/cockpit-agent/data-gaps');
            setItems(res?.data?.items || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchGaps(); }, [fetchGaps]);

    const handleItemClick = (item) => {
        if (!item.mandate_id) return;
        // Réécrit l'URL pour qu'elle pointe vers le bon basePath (agent ou admin)
        const url = (item.action_url || '').replace('/admin/', `${basePath}/`);
        navigate(url || `${basePath}/mandates/v2/${item.mandate_id}`);
    };

    const handleDismiss = () => {
        try { sessionStorage.setItem('homly_dq_banner_dismissed', '1'); } catch { /* quota */ }
        setDismissed(true);
    };

    if (loading || dismissed || items.length === 0) return null;

    const total = items.length;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.06] via-amber-500/[0.03] to-transparent p-4 sm:p-5 mb-4"
                data-testid="data-quality-banner"
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/20 shrink-0">
                            <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" strokeWidth={2} />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-sm sm:text-base font-bold text-foreground" data-testid="dq-title">
                                Complétez vos données — {total} action{total > 1 ? 's' : ''} suggérée{total > 1 ? 's' : ''}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                Quelques saisies rapides pour enrichir votre cockpit et activer pleinement vos copilotes IA.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                        data-testid="dq-dismiss"
                        aria-label="Masquer pour cette session"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>

                {/* Items list */}
                <ul className="space-y-2">
                    {items.slice(0, 4).map((item, idx) => {
                        const style = SEVERITY_STYLE[item.severity] || SEVERITY_STYLE.info;
                        const Icon = style.icon;
                        return (
                            <li key={`${item.type}-${item.mandate_id}-${idx}`}>
                                <button
                                    type="button"
                                    onClick={() => handleItemClick(item)}
                                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-card/60 border border-border hover:border-amber-500/30 hover:bg-card transition-all text-left group"
                                    data-testid={`dq-item-${idx}`}
                                >
                                    <Icon className={`w-4 h-4 shrink-0 ${style.iconClass}`} strokeWidth={2.2} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-foreground truncate">{item.label}</span>
                                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${style.chipClass} font-bold`}>
                                                {item.mandate_address}
                                            </span>
                                        </div>
                                        {item.hint && (
                                            <p className="text-xs text-muted-foreground mt-0.5 leading-snug truncate">
                                                {item.hint}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 opacity-70 group-hover:opacity-100 transition-opacity shrink-0">
                                        <span className="hidden sm:inline">{item.cta || 'Ouvrir'}</span>
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {total > 4 && (
                    <p className="text-[11px] text-muted-foreground mt-3 italic">
                        +{total - 4} autre{total - 4 > 1 ? 's' : ''} suggestion{total - 4 > 1 ? 's' : ''} disponible{total - 4 > 1 ? 's' : ''} dans vos mandats.
                    </p>
                )}
            </motion.div>
        </AnimatePresence>
    );
}
